/**
 * iter233 · Team notes + full audit trail for one production slot.
 * Notes are append-only (user · timestamp · note) — nothing overwrites.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PlanNotesDrawer({ planId, title, subtitle, onClose, onChanged }) {
  const [plan, setPlan] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/factory-plans/${planId}`, { withCredentials: true });
      setPlan(res.data);
    } catch { toast.error('Failed to load slot'); }
  }, [planId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/factory-plans/${planId}/notes`, { message: note.trim() }, { withCredentials: true });
      setNote('');
      await load();
      onChanged?.();
      toast.success('Note added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add note'); }
    finally { setBusy(false); }
  };

  const notes = [...(plan?.notes || [])].reverse();
  const history = [...(plan?.history || [])].reverse();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end justify-end" onClick={onClose}>
      <div className="ti-surface-modal border-l border-zinc-800 w-full max-w-lg h-full overflow-auto"
        onClick={e => e.stopPropagation()} data-testid="fp-notes-drawer">
        <div className="sticky top-0 ti-surface-modal border-b border-zinc-800 px-4 py-3 flex justify-between items-start">
          <div>
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Team notes & audit</p>
            <p className="font-heading text-sm text-zinc-100">{title}</p>
            <p className="text-[11px] text-zinc-400">{subtitle} · {planId}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-100 text-lg">×</button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-2">
            <input value={note} onChange={e => setNote(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') add(); }}
              data-testid="fp-notes-input" placeholder="Add a note for the team…"
              className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-amber-500" />
            <button onClick={add} disabled={busy || !note.trim()} data-testid="fp-notes-add"
              className="bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black text-xs font-semibold px-3 rounded-lg">
              Add
            </button>
          </div>

          <div className="space-y-2">
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Notes ({notes.length})</p>
            {notes.length === 0 && <p className="text-[11px] text-zinc-600">No notes yet.</p>}
            {notes.map((n, i) => (
              <div key={i} className="ti-surface-inset border border-zinc-800 rounded-lg p-2">
                <div className="flex justify-between text-[10px] text-zinc-500">
                  <span className="text-amber-300">{n.user_name || n.by || 'user'}{n.role ? ` · ${n.role}` : ''}</span>
                  <span>{(n.at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
                <p className="text-xs text-zinc-200 mt-0.5 whitespace-pre-wrap">{n.message || n.note}</p>
              </div>
            ))}
          </div>

          <div className="space-y-1">
            <p className="text-[10px] uppercase text-zinc-500 tracking-wider">Change history ({history.length})</p>
            {history.slice(0, 40).map((h, i) => (
              <div key={i} className="text-[11px] text-zinc-400 border-l border-zinc-800 pl-2 py-0.5">
                <span className="text-zinc-500">{(h.at || '').slice(0, 16).replace('T', ' ')}</span>
                {' · '}<span className="text-zinc-200">{h.action}</span>
                {' · '}{h.by || h.user}
                {h.note ? ` · ${h.note}` : ''}
                {h.before && h.after && (
                  <div className="text-[10px] text-zinc-500 font-mono truncate">
                    {Object.keys(h.after).slice(0, 4).map(k => (
                      <span key={k} className="mr-2">{k}: {String(h.before?.[k] ?? '—')} → {String(h.after[k])}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
